import { type ReactNode } from "react";
import { ChevronIcon } from "vega-icons";

import { type PopoverContentProps } from "@radix-ui/react-popover";
import { Checkbox } from "@vega-core/components/shadcn/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@vega-core/components/shadcn/popover";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

import { type NestedOptionType } from "../types";

import { OptionBadge } from "./OptionBadge";
import { SubOptionsPanel } from "./SubOptionsPanel";

type ParentOptionProps = {
  childPopoverContentProps?: PopoverContentProps;
  option: NestedOptionType;
  index: number;
  totalOptions: number;
  allSubOptionsSelected?: boolean;
  selectedCount: number;
  isSelected: boolean;
  onSelect: (checked: boolean) => void;
  onSelectOption: (option: NestedOptionType) => void;
  searchValue: string | null;
  setSearchValue: (value: string) => void;
  filteredSubOptions: NestedOptionType[] | undefined;
  isOptionSelected: (optionValue: string) => boolean;
  hideCheckbox?: boolean;
  renderLabel?: (option: NestedOptionType) => ReactNode;
  renderSubOptionLabel?: (
    option: NestedOptionType,
    parentValue: string,
  ) => ReactNode;
  selectedParent: string | null;
  setSelectedParent: (value: string | null) => void;
};

export const ParentOption = ({
  childPopoverContentProps,
  option,
  index,
  totalOptions,
  allSubOptionsSelected,
  selectedCount,
  isSelected,
  onSelect,
  onSelectOption,
  searchValue,
  setSearchValue,
  filteredSubOptions,
  isOptionSelected,
  hideCheckbox = false,
  renderLabel,
  renderSubOptionLabel,
  selectedParent,
  setSelectedParent,
}: ParentOptionProps) => {
  const hasSubOptions = option.subOptions && option.subOptions.length > 0;
  const isFirstItem = index === 0;
  const isLastItem = index === totalOptions - 1;
  const showSearch = option.subOptions && option.subOptions.length > 5;

  return (
    <Popover>
      <PopoverTrigger>
        <div
          className={cnTwMerge(
            "flex justify-between items-center cursor-pointer hover:bg-background/component",
            isFirstItem && "first:rounded-t-companyBorderRadius",
            isLastItem && "last:rounded-b-companyBorderRadius",
            selectedParent === option.value && "bg-background/component",
          )}
        >
          <div className="flex gap-3 items-center py-3 px-6 flex-grow body-base-medium text-text/primary min-w-0 justify-start">
            {!hideCheckbox && (
              <Checkbox
                checked={hasSubOptions ? allSubOptionsSelected : isSelected}
                onCheckedChange={(checked) => {
                  if (hasSubOptions) {
                    onSelect(!!checked);
                  } else {
                    onSelectOption(option);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
              />
            )}
            <span
              className="text-text/primary body-semi-large-medium truncate min-w-0 flex-1 text-left"
              title={option.label}
            >
              {renderLabel ? renderLabel(option) : option.label}
            </span>
            <div className="min-w-4.5 ml-2 flex-shrink-0">
              <OptionBadge count={selectedCount} />
            </div>
          </div>

          {hasSubOptions ? (
            <div className="pb-4 -rotate-90">
              <ChevronIcon className="h-4 w-4" />
            </div>
          ) : null}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        {...childPopoverContentProps}
        side={childPopoverContentProps?.side ?? "right"}
      >
        {/* Show suboptions when parent is hovered */}
        {hasSubOptions ? (
          <SubOptionsPanel
            filteredSubOptions={filteredSubOptions}
            isOptionSelected={isOptionSelected}
            onSelectOption={onSelectOption}
            parentOption={option}
            renderSubOptionLabel={renderSubOptionLabel}
            searchValue={searchValue || ""}
            selectedParent={selectedParent}
            setSearchValue={setSearchValue}
            setSelectedParent={setSelectedParent}
            showSearch={showSearch}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
};
